import React from 'react';

import cs from 'classnames';
import {colors} from 'js-libs/sweetcommons/styles';
import get from 'lodash/get';
import {browserHistory} from 'react-router';
import {CSSTransition} from 'react-transition-group';
import styled from 'styled-components';

import {getActivityTitleForNotification} from 'app/activity/shared-utils';
import {LazyTooltip} from 'app/components/general/LazyTooltip';
import {getElapsedTime} from 'app/general/dates';
import {getContentType} from 'app/utils/content-type';
import {capitalizeFirstLetter} from 'app/utils/strings';
import {getPathFromURL} from 'app/utils/urls';

import type {NotificationType} from 'app/notifly/types';
import type {contentType} from 'app/utils/content-type';

const getNotificationPhrase = (n) =>
  `${capitalizeFirstLetter(
    getActivityTitleForNotification(n)
  )} ${getElapsedTime(n.timestamp)}`;

function getNotificationsText(notifications: Array<NotificationType>) {
  return (
    <div className="small">
      {notifications.length === 1 ? (
        getNotificationPhrase(notifications[0])
      ) : (
        <ul className="text-left pl-3 mb-0">
          {([] as Array<NotificationType>)
            .concat(notifications)
            .reverse()
            .map((n, index) => (
              <li key={index}>{getNotificationPhrase(n)}</li>
            ))}
        </ul>
      )}
    </div>
  );
}

function goToLastNotificationURL(
  e: React.MouseEvent<any>,
  notifications: Array<NotificationType>
) {
  const actionURL = get(notifications[0], 'action_object.html_url');

  if (actionURL != null) {
    e.stopPropagation();
    e.preventDefault();
    browserHistory.push(getPathFromURL(actionURL));
  }
}

const StyledIconContainerSpan = styled.span`
  position: relative;

  .notifications {
    position: absolute;
    width: 10px;
    height: 10px;
    background: ${colors.lightLinkBlue};
    background: radial-gradient(
      ellipse at center,
      ${colors.lightLinkBlue} 0%,
      ${colors.linkBlue} 100%
    );
    overflow: hidden;
    border-radius: 50%;
    border: 2px solid #fff;

    &.notifications-enter,
    &.notifications-appear {
      opacity: 0.01;
      transform: scale(0.5);
    }

    &.notifications-enter-active,
    &.notifications-appear-active {
      opacity: 1;
      transform: scale(1);
      transition: all 500ms cubic-bezier(0.175, 0.885, 0.32, 1.5);
    }
  }
`;

interface Props {
  contentType: contentType;
  notifications?: Array<NotificationType>;
  className?: string;
}

export const DocumentIconWithNotifications = ({
  contentType,
  className,
  notifications = [],
}: Props) =>
  notifications.length > 0 ? (
    <LazyTooltip tooltip={getNotificationsText(notifications)}>
      <StyledIconContainerSpan
        onClick={(e) => goToLastNotificationURL(e, notifications)}
      >
        <CSSTransition
          appear={true}
          in={true}
          timeout={500}
          classNames="notifications"
        >
          <span className="notifications" />
        </CSSTransition>
        <DocumentIcon
          contentType={contentType}
          className={cs('fa-fw', className)}
        />
      </StyledIconContainerSpan>
    </LazyTooltip>
  ) : (
    <DocumentIcon
      contentType={contentType}
      className={cs('fa-fw', className)}
    />
  );

interface DocumentIconProps {
  contentType: contentType;
  className?: string;
}

export const DocumentIcon = ({
  contentType,
  className = '',
}: DocumentIconProps) => (
  <i className={cs(className, getContentType(contentType).icon)} />
);
